import { Action } from '@reduxjs/toolkit';
import { combineEpics, Epic } from 'redux-observable';
import { defer, filter, from, map, mergeMap, retry, takeUntil, timer, withLatestFrom } from 'rxjs';

import rooms from 'domains/wall_preview_rooms';
import stage from 'domains/stage';

import module from './index';
import * as api from './wall_previews.api';

const { actions, selectors } = module;

import { catchAndLogError } from 'utils/common';

import type { RootState } from 'store/wos_store';
import type { SaveWallPreviewPOSTData } from './wall_previews.types';

type WosEpic = Epic<Action, Action, RootState>;

const getAllWallPreviewsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.getAll.pending.match),
    mergeMap(() =>
      from(api.all()).pipe(
        map((previews) => actions.getAll.fulfilled(previews)),
        catchAndLogError(actions.getAll.rejected())
      )
    )
  );

const destroyWallPreviewEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.destroy.pending.match),
    mergeMap(({ payload }) =>
      from(api.destroy(payload)).pipe(
        map(() => actions.destroy.fulfilled(payload)),
        catchAndLogError(actions.destroy.rejected)
      )
    )
  );

const saveWallPreviewEpic: WosEpic = (action$, state$) =>
  action$.pipe(
    filter(actions.saveWallPreview.pending.match),
    withLatestFrom(state$),
    map(([{ payload }, state]): SaveWallPreviewPOSTData => {
      const whichWallPreview = rooms.selectors.whichWallPreview(state);
      if (!whichWallPreview) throw new Error('No Wall Preview');

      const wallPreviewDimensions = stage.selectors.wallPreviewDimensions(state);
      if (!wallPreviewDimensions) throw new Error('No Wall Preview Dimensions');

      const products = selectors.forSave(state);

      return {
        name: payload || whichWallPreview.description,
        wall_preview: {
          is_user_background: whichWallPreview.type === 'user',
          background_image: whichWallPreview.fullSrc,
          background_dimensions: wallPreviewDimensions,
          products,
        },
      };
    }),
    mergeMap((payload) => {
      return from(api.create(payload)).pipe(
        map((preview) => actions.saveWallPreview.fulfilled(preview)),
        catchAndLogError(actions.saveWallPreview.rejected())
      );
    })
  );

const retryPreviewLinkEpic: WosEpic = (action$) =>
  action$.pipe(
    filter(actions.saveWallPreview.fulfilled.match),
    map(({ payload }) => payload.id),
    mergeMap((id) =>
      // create observable for request, use defer to re-run the promise when retried
      defer(() => api.get(id, true)).pipe(
        retry({
          count: 5,
          delay: (error, retries) =>
            timer(5000 * retries).pipe(
              // Cancel the retries if this preview is removed
              takeUntil(
                action$.pipe(
                  filter(actions.destroy.pending.match),
                  filter(({ payload }) => payload === id)
                )
              )
            ),
        }),
        map(actions.updateWallPreview),
        catchAndLogError(actions.saveWallPreview.rejected())
      )
    )
  );

const combined = combineEpics(
  getAllWallPreviewsEpic,
  destroyWallPreviewEpic,
  saveWallPreviewEpic,
  retryPreviewLinkEpic
);
export default combined;
